import React, { useCallback } from 'react';
import { isOverflowNode, type SearchAttackPathNode } from './types';
import { CursorArrowRaysIcon } from '@heroicons/react/24/outline';
import { useChatStore, type ChatState } from '../../../store/chatStore';

import { useTranslation } from 'react-i18next';

const buildExplainAssetMessage = (prn: string): string =>
  `<usermessage>Explain this asset in the context of the attack path please.</usermessage><context>${prn}</context>`;

export const AssetActionInstruction = ({
  node,
  closeTooltip,
}: {
  node: SearchAttackPathNode;
  closeTooltip?: () => void;
}) => {
  const { t } = useTranslation('pleri/chat');
  const isModifierGroup = node.graphProps?.assetType === 'modifier-group';
  const isVirtualNode = node.graphProps?.type === 'virtualAsset';
  const hasPrn = !!node.graphProps?.id?.startsWith('prn:assets');

  const activeConversationId = useChatStore(
    (s: ChatState) => s.activeConversationId,
  );
  const addMessage = useChatStore((s: ChatState) => s.addMessage);
  const createConversation = useChatStore(
    (s: ChatState) => s.createConversation,
  );

  const handleAskClick = useCallback(() => {
    const prn = node.graphProps?.id;
    if (typeof prn !== 'string' || !prn.startsWith('prn:assets')) {
      return;
    }
    const messageText = buildExplainAssetMessage(prn);
    if (!activeConversationId) {
      const newId = createConversation();
      addMessage(newId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    } else {
      addMessage(activeConversationId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    }
    closeTooltip?.();
  }, [
    node.graphProps?.id,
    activeConversationId,
    addMessage,
    createConversation,
    closeTooltip,
  ]);

  if (isOverflowNode(node)) {
    return (
      <div className="flex flex-col gap-3 border-t pt-2">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <CursorArrowRaysIcon className="h-3 w-3" />
          {t('clickOnTheNodeToShowAll')}
        </div>
      </div>
    );
  }
  if (isVirtualNode) {
    return <></>;
  }
  if (isModifierGroup) {
    return (
      <div className="flex flex-col gap-3 border-t pt-2">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <CursorArrowRaysIcon className="h-3 w-3" />
          {t('clickOnTheNodeToExpandOr')}
        </div>
      </div>
    );
  }
  if (hasPrn) {
    return (
      <div className="flex flex-col gap-3 border-t pt-2">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <button
            type="button"
            onClick={handleAskClick}
            className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-1 focus:ring-gray-500 focus:ring-offset-0 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:focus:ring-gray-400"
          >
            <CursorArrowRaysIcon className="h-3.5 w-3.5 shrink-0" />
            <span>{t('explainToMe')}</span>
          </button>
        </div>
      </div>
    );
  }
  return <></>;
};
